import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Target } from 'lucide-react';
import { usePipelineStages } from '@/hooks/usePipelineStages';

interface PipelineFunnelProps {
  pipelineStages: Record<string, number>;
  total: number;
}

const STAGE_COLORS: Record<string, string> = {
  'New': 'bg-blue-500',
  'Contacted': 'bg-cyan-500',
  'Connected': 'bg-lime-500',
  'Qualified': 'bg-amber-500',
  'Trial Received': 'bg-teal-500',
  'Proposal': 'bg-purple-500',
  'Negotiation': 'bg-indigo-500',
  'Closed Won': 'bg-green-600',
  'Closed Lost': 'bg-red-500',
};

const PipelineFunnel = ({ pipelineStages, total }: PipelineFunnelProps) => {
  const navigate = useNavigate();
  const { stages: orderedStages } = usePipelineStages();

  // Sort entries by API stage order, then alphabetically for unknown stages
  const entries = Object.entries(pipelineStages).sort(([a], [b]) => {
    const idxA = orderedStages.indexOf(a);
    const idxB = orderedStages.indexOf(b);
    if (idxA >= 0 && idxB >= 0) return idxA - idxB;
    if (idxA >= 0) return -1;
    if (idxB >= 0) return 1;
    return a.localeCompare(b);
  });
  const maxValue = entries.length > 0 ? Math.max(...entries.map(([, v]) => v)) : 1;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base md:text-lg flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          Pipeline Distribution
          <span className="ml-auto text-sm font-normal text-muted-foreground">
            {total} leads
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {entries.map(([stage, count], i) => {
          const pct = Math.max((count / maxValue) * 100, 4);
          return (
            <div
              key={stage}
              className="flex items-center gap-3 cursor-pointer hover:bg-accent/50 rounded-md px-1 -mx-1 transition-colors"
              onClick={() => navigate(`/app/leads?current_stage=${encodeURIComponent(stage)}`)}
            >
              <span className="text-xs md:text-sm text-muted-foreground w-24 md:w-28 truncate shrink-0">
                {stage}
              </span>
              <div className="flex-1 h-5 md:h-6 bg-muted rounded-sm overflow-hidden">
                <div
                  className={`h-full ${STAGE_COLORS[stage] || 'bg-gray-500'} rounded-sm transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs md:text-sm font-medium w-10 text-right shrink-0">
                {count}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default PipelineFunnel;
